import { ImagesLayout } from 'components/library/images/ImagesLayout';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentImageLibraryApiResponse } from 'services/documents/DocumentRepository';
import { useDocumentImageLibrary } from './useDocumentImageLibrary';
import ImageUploadButton from '../ImageLibrary/ImageUploadButton/ImageUploadButton';
import { DraggableImage } from './DraggableImage';
import { ImagesListErrorState } from 'components/library/images-list/ImagesListErrorState';
import { ImagesGallerySkeleton } from 'components/library/images-list/ImagesGallerySkeleton';
import { ThisDocumentImagesListEmptyState } from 'components/library/images-list/ThisDocumentImagesListEmptyState';

enum UIState {
  LOADING = 'loading',
  ERROR = 'error',
  EMPTY = 'empty',
  READY = 'ready',
}

export default function ThisDocumentImages() {
  const contentId = useAppSelector(selectContentId);

  const { result, isError, isFetched } = useDocumentImageLibrary(contentId);
  const { t } = useTranslation();
  const documentImages = result;

  const uiState = useMemo(() => {
    if (!isFetched) return UIState.LOADING;
    if (isError) return UIState.ERROR;
    if (result.length === 0) return UIState.EMPTY;
    return UIState.READY;
  }, [isFetched, isError, result]);

  // Filter out unique image grid blocks
  const imageSrcList = useMemo(() => {
    const uniqueUrls = new Set<string>();

    if (isFetched && result) {
      const images = documentImages as DocumentImageLibraryApiResponse;
      if (Array.isArray(images)) {
        images.forEach((image) => {
          uniqueUrls.add(image.link);
        });
      }
    }

    return uniqueUrls;
  }, [isFetched, result]);

  if (uiState === UIState.LOADING) {
    return <ImagesGallerySkeleton />;
  }

  if (uiState === UIState.ERROR) {
    return <ImagesListErrorState title="Error" description={t('document.image.errors.library_image_load_fail')} />;
  }

  if (uiState === UIState.EMPTY) {
    return <ThisDocumentImagesListEmptyState />;
  }

  return (
    <ImagesLayout.Gallery>
      <ImagesLayout.GalleryTile key={-1}>
        <ImageUploadButton data-testid={'upload-button'} />
      </ImagesLayout.GalleryTile>
      {Array.from(imageSrcList).map((src, index) => (
        <DraggableImage key={index} url={src} title={index.toString()} />
      ))}
    </ImagesLayout.Gallery>
  );
}
